import { motion } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { HERO_SIDE_LIST } from "@/constants/aero";

const ease = [0.16, 1, 0.3, 1];

export const AeroHero = () => (
  <section id="home" data-testid="aero-hero" className="relative overflow-hidden h-[100svh] bg-[#0B0E14] z-[45]">
    {/* Full-bleed looping FPV trailer video */}
    <div className="absolute inset-0">
      <motion.video
        initial={{ scale: 1.05 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease }}
        className="absolute inset-0 h-full w-full object-cover"
        poster="/assets/hero-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label="Cinematic FPV drone showreel by Akhilesh"
        data-testid="aero-hero-video"
      >
        <source src="/assets/hero-loop.webm" type="video/webm" />
        <source src="/assets/hero-loop.mp4" type="video/mp4" />
      </motion.video>
    </div>

    {/* dark overlay (~15%) + directional gradients for text readability */}
    <div className="absolute inset-0 bg-black/15" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-black/10" />
    <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />

    {/* Content — bottom-left on mobile, centered-left on desktop */}
    <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 w-full h-full flex items-end md:items-center pb-14 md:pb-0 md:pt-[76px]">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.15, ease }}
        className="max-w-2xl"
      >
        <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-goldlight flex items-center gap-4 [text-shadow:_0_1px_12px_rgba(0,0,0,0.6)]">
          <span className="w-10 h-px bg-goldlight/70 inline-block" /> Aerial Cinematographer
        </p>
        <h1 className="mt-5 md:mt-6 font-display font-black uppercase leading-[0.98] tracking-tight text-4xl sm:text-6xl lg:text-7xl text-white [text-shadow:_0_3px_30px_rgba(0,0,0,0.5)]">
          Stories<br />Look Better<br />
          <span className="font-medium text-white/70">From Above.</span>
        </h1>
        <p className="mt-4 md:mt-6 text-white/85 max-w-md leading-relaxed text-[15px] md:text-base [text-shadow:_0_1px_14px_rgba(0,0,0,0.55)]">
          Turning real locations into cinematic experiences for Films, OTT, Documentaries, Brands and Beyond.
        </p>
        <div className="mt-7 md:mt-9 flex flex-wrap items-center gap-4">
          <a
            data-testid="aero-hero-showreel"
            href="https://youtu.be/oCCiNLuwlqQ"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-ink font-mono text-[13px] font-bold tracking-[0.18em] uppercase px-7 py-4 rounded-full hover:bg-goldlight transition-colors"
          >
            <Play size={12} fill="currentColor" /> Watch Showreel
          </a>
          <a
            data-testid="aero-hero-book-shoot"
            href="/book-shoot"
            className="inline-flex items-center gap-2 border border-white/50 text-white font-mono text-[13px] font-bold tracking-[0.18em] uppercase px-7 py-4 rounded-full hover:border-goldlight hover:text-goldlight transition-colors"
          >
            Book a Shoot <ArrowUpRight size={13} />
          </a>
          <a
            data-testid="aero-hero-consultation"
            href="/services#consultation"
            className="inline-flex items-center gap-2 border border-[#F5B301]/70 text-[#F5B301] font-mono text-[13px] font-bold tracking-[0.18em] uppercase px-7 py-4 rounded-full hover:bg-[#F5B301] hover:text-black transition-all duration-300"
          >
            Book a Consultation <ArrowUpRight size={13} />
          </a>
        </div>
      </motion.div>
    </div>

    {/* right vertical list */}
    <motion.ul
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 1, delay: 0.5, ease }}
      className="absolute right-5 xl:right-8 top-[38%] -translate-y-1/2 z-20 hidden xl:flex flex-col gap-5"
      data-testid="aero-hero-side-list"
    >
      {HERO_SIDE_LIST.map((item, i) => (
        <li key={item} className="flex items-center justify-end gap-3 [text-shadow:_0_1px_10px_rgba(0,0,0,0.55)]">
          <span className={`font-mono text-[12px] tracking-[0.25em] uppercase ${i === 0 ? "text-white font-bold" : "text-white/75"}`}>
            {item}
          </span>
          <span className={`w-1.5 h-1.5 rotate-45 ${i === 0 ? "bg-goldlight" : "bg-goldlight/60"}`} />
        </li>
      ))}
    </motion.ul>
  </section>
);
